import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ThumbsUp, ThumbsDown, CheckCircle2, 
  Lock, LogIn, BarChart3, Users, Share2, Sparkles, Check
} from 'lucide-react';
import api from '../utils/api';
import { isUserLoggedIn } from '../utils/auth';
import { showSuccess, showError } from '../utils/toast';

export default function VillageSurveyCard({ village, compact = false }) {
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const villageSlug = village?.slug;

  const fetchSurvey = async () => {
    if (!villageSlug) return;
    try {
      const res = await api.get(`/villages/${villageSlug}/survey`);
      if (res.data?.success) {
        setSurvey(res.data);
      }
    } catch (err) {
      console.error('Error fetching village survey:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSurvey();
  }, [villageSlug]);

  const handleVote = async (responseOption) => {
    // Check if user is logged in
    if (!isUserLoggedIn()) {
      setShowLoginModal(true);
      return;
    }

    try {
      setVoting(true);
      const res = await api.post(`/villages/${villageSlug}/survey`, {
        response: responseOption,
      });

      if (res.data?.success) {
        setSurvey(res.data);
        showSuccess(res.data.message || 'आपकी राय सफलतापूर्वक दर्ज कर ली गई है!');
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || 'वोट दर्ज करने में त्रुटि हुई। कृपया पुनः प्रयास करें।';
      showError(errMsg);
    } finally {
      setVoting(false);
    }
  };

  const handleSharePoll = () => {
    if (!village) return;
    const shareText = encodeURIComponent(
      `🗳️ *ग्राम पंचायत ${village.name} जनमत पोल*\nसवाल: क्या आप मौजूदा सरपंच से संतुष्ट हैं?\n\n👉 *वोट दें व रिजल्ट देखें:*\n${window.location.href}`
    );
    window.open(`https://wa.me/?text=${shareText}`, '_blank');
  };

  if (loading) {
    if (compact) {
      return (
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 border border-gray-200 dark:border-gray-800 shadow-md animate-pulse flex flex-col justify-between min-h-[190px]">
          <div>
            <div className="h-4 bg-gray-200 dark:bg-gray-800 rounded w-1/2 mb-3" />
            <div className="h-5 bg-gray-200 dark:bg-gray-800 rounded w-3/4 mb-2" />
            <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/3" />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="h-12 bg-gray-100 dark:bg-gray-800 rounded-xl" />
            <div className="h-12 bg-gray-100 dark:bg-gray-800 rounded-xl" />
          </div>
        </div>
      );
    }
    return (
      <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 sm:p-8 border border-gray-200 dark:border-gray-800 shadow-sm animate-pulse">
        <div className="h-6 bg-gray-200 dark:bg-gray-800 rounded w-1/3 mb-4" />
        <div className="h-8 bg-gray-200 dark:bg-gray-800 rounded w-2/3 mb-6" />
        <div className="space-y-3">
          <div className="h-14 bg-gray-100 dark:bg-gray-800/60 rounded-2xl" />
          <div className="h-14 bg-gray-100 dark:bg-gray-800/60 rounded-2xl" />
        </div>
      </div>
    );
  }

  const loggedIn = isUserLoggedIn();
  const totalVotes = survey?.totalVotes || 0;
  const stats = survey?.stats || {
    yes: { count: 0, percent: 0 },
    no: { count: 0, percent: 0 },
  };
  const userVote = survey?.userVote; // 'yes' | 'no' | null

  if (compact) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 border border-gray-200 dark:border-gray-800 shadow-md flex flex-col justify-between group hover:border-amber-400 dark:hover:border-amber-600 transition-all relative">
        <div>
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
              <BarChart3 className="w-5 h-5 flex-shrink-0" />
              <span className="text-xs font-bold uppercase tracking-wider">सरपंच समीक्षा</span>
            </div>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border border-amber-200/60 dark:border-amber-800/60">
              {totalVotes} {totalVotes === 1 ? 'वोट' : 'कुल वोट'}
            </span>
          </div>

          <h3 className="font-bold text-gray-900 dark:text-white text-base leading-snug">
            मौजूदा सरपंच से संतुष्ट हैं?
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-1">
            गाँव {village?.name} के विकास पर अपनी राय दें
          </p>

          {/* Voting Options: 2 compact pills */}
          <div className="grid grid-cols-2 gap-2 mt-3">
            {/* Yes */}
            <button
              type="button"
              disabled={voting}
              onClick={() => handleVote('yes')}
              className={`p-2 rounded-xl text-center flex flex-col items-center justify-center transition-all border ${
                userVote === 'yes'
                  ? 'border-emerald-500 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-500 font-bold'
                  : 'border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/50 hover:border-emerald-400 hover:bg-emerald-50/40 dark:hover:bg-emerald-950/30'
              }`}
              title="हाँ, सरपंच के कामकाज से संतुष्ट हैं"
            >
              <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-bold text-xs sm:text-sm">
                {userVote === 'yes' ? <Check className="w-4 h-4 stroke-[3]" /> : <ThumbsUp className="w-4 h-4" />}
                <span>हाँ</span>
              </div>
              <span className="text-xs font-black text-gray-800 dark:text-gray-100 mt-1">
                {stats.yes.percent}%
              </span>
            </button>

            {/* No */}
            <button
              type="button"
              disabled={voting}
              onClick={() => handleVote('no')}
              className={`p-2 rounded-xl text-center flex flex-col items-center justify-center transition-all border ${
                userVote === 'no'
                  ? 'border-rose-500 bg-rose-500/15 text-rose-700 dark:text-rose-300 ring-2 ring-rose-500 font-bold'
                  : 'border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-800/50 hover:border-rose-400 hover:bg-rose-50/40 dark:hover:bg-rose-950/30'
              }`}
              title="नहीं, संतुष्ट नहीं हैं"
            >
              <div className="flex items-center gap-1.5 text-rose-600 dark:text-rose-400 font-bold text-xs sm:text-sm">
                {userVote === 'no' ? <Check className="w-4 h-4 stroke-[3]" /> : <ThumbsDown className="w-4 h-4" />}
                <span>नहीं</span>
              </div>
              <span className="text-xs font-black text-gray-800 dark:text-gray-100 mt-1">
                {stats.no.percent}%
              </span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-xs">
          {loggedIn ? (
            <span className="text-emerald-600 dark:text-emerald-400 font-semibold flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {userVote ? 'आपका वोट दर्ज है' : 'क्लिक कर वोट दें'}
            </span>
          ) : (
            <Link
              to={`/login?redirect=/village/${villageSlug}`}
              className="text-primary-600 dark:text-primary-400 font-bold flex items-center gap-1 hover:underline"
            >
              <LogIn className="w-3.5 h-3.5" /> लॉगिन कर वोट दें
            </Link>
          )}

          <button
            onClick={handleSharePoll}
            className="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1 hover:underline ml-auto"
            title="सर्वे को WhatsApp पर शेयर करें"
          >
            <Share2 className="w-3.5 h-3.5" /> शेयर
          </button>
        </div>

        {/* Login Modal */}
        {showLoginModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn" onClick={() => setShowLoginModal(false)}>
            <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-800 text-center relative" onClick={(e) => e.stopPropagation()}>
              <div className="w-14 h-14 bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7" />
              </div>

              <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">
                लॉगिन आवश्यक है (Login Required)
              </h3>
              
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                गाँव <strong>{village?.name}</strong> के सरपंच संतुष्टि सर्वे में भाग लेने के लिए कृपया पहले लॉगिन करें।
                <br />
                <span className="text-xs text-gray-400 mt-1 block">
                  ताकि सर्वेक्षण 100% निष्पक्ष व प्रामाणिक बना रहे।
                </span>
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to={`/login?redirect=/village/${villageSlug}`}
                  className="w-full sm:w-auto px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-sm shadow-md transition-all flex items-center justify-center gap-2"
                >
                  <LogIn className="w-4 h-4" />
                  लॉगिन करें (Login)
                </Link>
                <button
                  type="button"
                  onClick={() => setShowLoginModal(false)}
                  className="w-full sm:w-auto px-5 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-sm transition-all"
                >
                  बंद करें (Close)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white via-white to-amber-50/30 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950 rounded-3xl p-5 sm:p-8 border border-gray-200 dark:border-gray-800 shadow-lg relative overflow-hidden">
      {/* Decorative background glow */}
      <div className="absolute -top-24 -right-24 w-60 h-60 bg-primary-500/10 dark:bg-primary-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Header Badge & Title */}
      <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60 mb-2">
            <BarChart3 className="w-3.5 h-3.5" />
            <span>जनमत सर्वेक्षण &bull; Gram Panchayat Poll</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-gray-900 dark:text-white leading-tight">
            क्या आप अपने गाँव <span className="text-primary-600 dark:text-primary-400">{village?.name}</span> के मौजूदा सरपंच से संतुष्ट हैं?
          </h2>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
            गाँव के विकास, कार्यशैली व जनसेवा पर अपनी स्वतंत्र राय दर्ज करें।
          </p>
        </div>

        {/* Total Votes Count & WhatsApp Share */}
        <div className="flex items-center gap-2 sm:self-start shrink-0">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-xl text-xs font-bold text-gray-700 dark:text-gray-300">
            <Users className="w-3.5 h-3.5 text-primary-500" />
            <span>{totalVotes} {totalVotes === 1 ? 'वोट' : 'कुल वोट'}</span>
          </div>
          <button
            onClick={handleSharePoll}
            className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold transition-all shadow-sm active:scale-95"
            title="सर्वे को WhatsApp पर शेयर करें"
          >
            <Share2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">शेयर करें</span>
          </button>
        </div>
      </div>

      {/* VOTING OPTIONS */}
      <div className="space-y-3 relative z-10">
        
        {/* OPTION 1: YES (हाँ, संतुष्ट हैं) */}
        <button
          type="button"
          disabled={voting}
          onClick={() => handleVote('yes')}
          className={`w-full group text-left relative overflow-hidden rounded-2xl p-4 transition-all border ${
            userVote === 'yes'
              ? 'border-emerald-500 bg-emerald-500/10 dark:bg-emerald-500/15 ring-2 ring-emerald-500 shadow-md'
              : 'border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-gray-800/40 hover:border-emerald-400 hover:bg-emerald-50/50 dark:hover:bg-emerald-950/20'
          }`}
        >
          {/* Animated Background Progress Fill */}
          <div
            className="absolute left-0 top-0 bottom-0 bg-emerald-500/15 dark:bg-emerald-500/25 transition-all duration-700 pointer-events-none rounded-2xl"
            style={{ width: `${stats.yes.percent}%` }}
          />

          <div className="relative z-10 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg transition-transform group-hover:scale-105 ${
                  userVote === 'yes'
                    ? 'bg-emerald-500 text-white shadow-emerald-500/30 shadow'
                    : 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400'
                }`}
              >
                {userVote === 'yes' ? <Check className="w-5 h-5 stroke-[3]" /> : <ThumbsUp className="w-5 h-5" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 dark:text-white text-base">हाँ (Yes)</span>
                  {userVote === 'yes' && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-500 text-white tracking-wide uppercase">
                      आपका वोट
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">मैं सरपंच के कामकाज से संतुष्ट हूँ</p>
              </div>
            </div>

            {/* Percentage & Vote Count */}
            <div className="text-right shrink-0">
              <div className="text-lg font-black text-emerald-600 dark:text-emerald-400">
                {stats.yes.percent}%
              </div>
              <div className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">
                {stats.yes.count} वोट
              </div>
            </div>
          </div>
        </button>

        {/* OPTION 2: NO (नहीं, असंतुष्ट हैं) */}
        <button
          type="button"
          disabled={voting}
          onClick={() => handleVote('no')}
          className={`w-full group text-left relative overflow-hidden rounded-2xl p-4 transition-all border ${
            userVote === 'no'
              ? 'border-rose-500 bg-rose-500/10 dark:bg-rose-500/15 ring-2 ring-rose-500 shadow-md'
              : 'border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-gray-800/40 hover:border-rose-400 hover:bg-rose-50/50 dark:hover:bg-rose-950/20'
          }`}
        >
          {/* Animated Background Progress Fill */}
          <div
            className="absolute left-0 top-0 bottom-0 bg-rose-500/15 dark:bg-rose-500/25 transition-all duration-700 pointer-events-none rounded-2xl"
            style={{ width: `${stats.no.percent}%` }}
          />

          <div className="relative z-10 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg transition-transform group-hover:scale-105 ${
                  userVote === 'no'
                    ? 'bg-rose-500 text-white shadow-rose-500/30 shadow'
                    : 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-400'
                }`}
              >
                {userVote === 'no' ? <Check className="w-5 h-5 stroke-[3]" /> : <ThumbsDown className="w-5 h-5" />}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-gray-900 dark:text-white text-base">नहीं (No)</span>
                  {userVote === 'no' && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-500 text-white tracking-wide uppercase">
                      आपका वोट
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">मैं वर्तमान कार्यशैली से संतुष्ट नहीं हूँ</p>
              </div>
            </div>

            {/* Percentage & Vote Count */}
            <div className="text-right shrink-0">
              <div className="text-lg font-black text-rose-600 dark:text-rose-400">
                {stats.no.percent}%
              </div>
              <div className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">
                {stats.no.count} वोट
              </div>
            </div>
          </div>
        </button>

      </div>

      {/* FOOTER NOTICE / LOGIN PROMPT CALLOUT */}
      <div className="mt-5 pt-4 border-t border-gray-100 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-2">
          {loggedIn ? (
            <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-semibold">
              <CheckCircle2 className="w-4 h-4" />
              {userVote 
                ? 'आपका वोट सफलतापूर्वक दर्ज है। आप चाहें तो विकल्प बदलकर अपडेट कर सकते हैं।' 
                : 'आप लॉगिन हैं। किसी भी विकल्प पर क्लिक करके अपना वोट दें।'}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-medium">
              <Lock className="w-3.5 h-3.5" />
              केवल लॉगिन किए हुए ग्रामवासी ही वोट दे सकते हैं।
            </span>
          )}
        </div>

        {!loggedIn && (
          <Link
            to={`/login?redirect=/villages/${villageSlug}`}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold transition-all shadow-sm self-start sm:self-auto"
          >
            <LogIn className="w-3.5 h-3.5" />
            <span>वोट देने के लिए लॉगिन करें</span>
          </Link>
        )}
      </div>

      {/* LOGIN MODAL (Appears if guest user clicks an option) */}
      {showLoginModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white dark:bg-gray-900 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-800 text-center relative">
            <div className="w-14 h-14 bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7" />
            </div>

            <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">
              लॉगिन आवश्यक है (Login Required)
            </h3>
            
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              गाँव <strong>{village?.name}</strong> के सरपंच संतुष्टि सर्वे में भाग लेने के लिए कृपया पहले लॉगिन करें। 
              <br />
              <span className="text-xs text-gray-400 mt-1 block">
                ताकि सर्वेक्षण 100% निष्पक्ष व प्रामाणिक बना रहे।
              </span>
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to={`/login?redirect=/villages/${villageSlug}`}
                className="w-full sm:w-auto px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-bold text-sm shadow-md transition-all flex items-center justify-center gap-2"
              >
                <LogIn className="w-4 h-4" />
                लॉगिन करें (Login)
              </Link>
              <button
                type="button"
                onClick={() => setShowLoginModal(false)}
                className="w-full sm:w-auto px-5 py-2.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-semibold text-sm transition-all"
              >
                बंद करें (Close)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
